import { GeneratedSchedugramServerClient } from "./generated-schedugram-server-client";
import type {
  components,
  paths,
} from "./generated/schedugram-server-openapi-types";

const generatedSchedugramServerClient =
  new GeneratedSchedugramServerClient<paths>();

export interface BestTimeSlotApi {
  online: number;
  day: number;
  offset: number;
}

export interface BestTimeSlotApiWithIndex extends BestTimeSlotApi {
  index: number;
}

export interface ScheduleApi {
  day: number;
  timeSlots: number[];
}

type QueueScheduleSettingsResponse =
  components["schemas"]["QueueScheduleSettingsResponse"];
type SaveQueueScheduleRequestBody =
  components["schemas"]["SaveQueueScheduleRequestBody"];

export async function getQueueSetting(accountId: string): Promise<{
  enabled: boolean;
  schedules: ScheduleApi[];
  timezone: string;
  notifyEmptyQueue: boolean;
  bttp: BestTimeSlotApi[];
}> {
  const result = await generatedSchedugramServerClient.client.GET(
    "/insta-schedule/{accountId}",
    {
      params: {
        path: { accountId },
        query: { addBttp: "true" },
      },
    }
  );

  return toQueueSettings(await generatedSchedugramServerClient.unwrap(result));
}

export async function updateQueueSettings(
  accountId: string,
  params: {
    enabled: boolean;
    timezone?: string;
    schedules: Array<{ day: number; timeSlots: number[] }>;
    notifyEmptyQueue: boolean;
  }
) {
  const schedules = params.schedules
    .filter((schedule) => schedule.timeSlots.length > 0)
    .map((schedule) => toScheduleSlot(schedule));

  const body: SaveQueueScheduleRequestBody = {
    enabled: params.enabled,
    schedules,
    timezone: params.timezone,
    notifyEmptyQueue: params.notifyEmptyQueue,
  };

  const result = await generatedSchedugramServerClient.client.POST(
    "/insta-schedule/{accountId}",
    {
      params: {
        path: { accountId },
      },
      body,
    }
  );

  await generatedSchedugramServerClient.ensureOk(result);
}

export async function clearAccountQueue(accountId: string) {
  const result = await generatedSchedugramServerClient.client.DELETE(
    "/insta-schedule/{accountId}/clear",
    {
      params: {
        path: { accountId },
      },
    }
  );

  await generatedSchedugramServerClient.ensureOk(result);
}

// Reduce a schedule to the { day, timeSlots } contract shape, dropping the
// Mongo `_id` (and any other excess property) the server serialises onto GET
// responses. tsoa's validator for SaveQueueScheduleRequestBody rejects that
// `_id` as an excess property (400 ValidateError), so both the GET-normalise
// and the POST-save paths funnel through here to keep saves valid. (SKE-4075)
function toScheduleSlot(schedule: { day: number; timeSlots: number[] }) {
  return { day: schedule.day, timeSlots: schedule.timeSlots };
}

function toQueueSettings(settings: QueueScheduleSettingsResponse) {
  return {
    enabled: settings.enabled ?? false,
    schedules: (settings.schedules ?? []).map((schedule) =>
      toScheduleSlot(schedule)
    ),
    timezone: settings.timezone ?? "",
    notifyEmptyQueue: settings.notifyEmptyQueue ?? false,
    bttp: settings.bttp ?? [],
  };
}
